import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import * as myGlobals from 'src/app/globals';
import { DownloadInfoModel, DownloadInitModel, DownloadInitResponseModel, DownloadModel, PasswordModel } from 'src/app/models/download.model';
import { StorageModel } from 'src/app/models/storage.model';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as SgeCrypto from 'src/assets/lib/sge/sge-crypto';

import { CommonService } from './common.service';
import * as fs from './file-system-api';
import { NotificationService } from './notification.service';
@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  private MAX_DOWNLOAD_RETRY = 1; // Maximum number of download retries
  private NUMBER_OF_CHUNKS = 4; // Number of chunks to download at a time
  subscription: Subscription = new Subscription();
  selectedItems?: StorageModel[];
  downloadChunks: DownloadModel[] = [];
  downloadSubscriptions: { [downloadId: string]: Subscription } = {};
  notifier$ = new Subject<void>();

  downloadItems$: BehaviorSubject<DownloadModel[]> = new BehaviorSubject<DownloadModel[]>([]);
  shareId!: string;
  objectId!: string;
  password!: string;
  isEncrypted = false;
  isDownloading = false;

  constructor(private http: HttpClient, private notification: NotificationService, private commonService: CommonService) {}

  commonMethodSubscription(downloadInit: any, selectedItems: any[]): void {
    this.subscription = this.downloadInitAPI(downloadInit).subscribe({
      next: (response) => {
        const chunks: DownloadInfoModel[] = [];
        for (let i = 1; i <= response.chunkCount; i++) {
          const data: DownloadInfoModel = {
            chunkIndex: i,
            data: new Uint8Array(),
            status: 'pending',
            progress: 0,
          };
          chunks.push(data);
        }
        const data: DownloadModel = {
          downloadId: response.downloadId,
          status: 'pending',
          data: selectedItems.length === 1 && !selectedItems[0].directory ? chunks : [],
          chunkCount: response.chunkCount,
          fileName: selectedItems.length === 1 && !selectedItems[0].directory ? selectedItems[0].objectName : 'safir-depo.zip',
          totalSize: response.totalSize,
          progress: 0,
          zippedObject: selectedItems.length > 1 || selectedItems[0].directory,
        };
        this.downloadChunks.push(data);
        if (selectedItems.length === 1 && !selectedItems[0].directory) {
          if (this.isEncrypted) {
            SgeCrypto.startFileDecryption(response.downloadId, selectedItems[0].encryptedId)
              .then(() => {
                this.sendChunk(response.downloadId);
              })
              .catch(() => {
                const message = {
                  message: 'Bir hata oluştu. Lütfen tekrar deneyiniz.',
                };
                this.notification.errorNotification(message);
              });
          } else {
            this.sendChunk(response.downloadId);
          }
        } else if (selectedItems.length > 1 || (selectedItems.length === 1 && selectedItems[0].directory)) {
          this.downloadObjects(response.downloadId);
        }
      },
    });
  }

  downloadInit(selectedItems: any[] = []): void {
    this.isDownloading = true;
    for (const obj of selectedItems) {
      this.isEncrypted = obj.encrypted;
    }
    const downloadInit: DownloadInitModel = {} as DownloadInitModel;
    downloadInit.applicationName = 'storage';
    downloadInit.downloadType = 'ATTACHMENT';
    downloadInit.objects = selectedItems.map((item) => item.objectId);
    if (fs.isSupported()) {
      //using fs
      let file!: any;
      if (selectedItems.length === 1) {
        fs.createDummyFile(selectedItems[0]?.objectId, selectedItems[0]?.size)
          .then(() => {
            //success
            this.commonMethodSubscription(downloadInit, selectedItems);
          })
          .catch((error) => {
            this.notification.errorNotification({ message: this.commonService.getTranslatedMessage('DOWNLOAD_SIZE_TO_BIG_FOR_CHROME') });
          });
      } else {
        this.commonMethodSubscription(downloadInit, selectedItems);
      }
    } else {
      //Using ArrayBuffer
      let file!: any;
      try {
        file = new Uint8Array(selectedItems[0].size); //firefox supports max  8.5 gb
        this.commonMethodSubscription(downloadInit, selectedItems);
      } catch {
        this.isDownloading = false;
        this.notification.errorNotification({ message: this.commonService.getTranslatedMessage('DOWNLOAD_SIZE_TO_BIG_FOR_FIREFOX') });
      }
    }
  }

  sendChunk(downloadId: string): void {
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === downloadId);
    const pendingChunks = this.downloadChunks[arrIndex].data.filter((chunk) => chunk.status === 'pending');
    const startIndex = pendingChunks.sort((a, b) => a.chunkIndex - b.chunkIndex)[0].chunkIndex;
    const numberOfChunks =
      startIndex + this.NUMBER_OF_CHUNKS > this.downloadChunks[arrIndex].data.length ? this.downloadChunks[arrIndex].data.length : startIndex + this.NUMBER_OF_CHUNKS;
    for (let chunkIndex = startIndex; chunkIndex <= numberOfChunks; chunkIndex++) {
      this.getChunkToDownload(downloadId, chunkIndex);
    }
  }

  private getChunkToDownload(downloadId: string, chunkIndex: number, retryCount = 0): void {
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === downloadId);
    const chunkData = this.downloadChunks[arrIndex].data[chunkIndex - 1];
    const chunk = this.downloadChunks[arrIndex];
    if (chunkData.status !== 'completed' && chunk.status !== 'paused' && chunk.status !== 'cancelled') {
      this.downloadChunk(downloadId, arrIndex, chunkIndex, retryCount);
    } else {
      this.isDownloading = false;
    }
  }

  private downloadChunk(downloadId: string, arrIndex: number, chunkIndex: number, retryCount: number): void {
    this.downloadSubscriptions[downloadId] = new Subscription();
    let progress = 0;
    this.downloadSubscriptions[downloadId].add(
      this.downloadChunkAPI(downloadId, chunkIndex).subscribe({
        next: (response) => {
          this.downloadChunks[arrIndex].data[chunkIndex - 1].status = 'downloading';
          this.downloadChunks[arrIndex].data[chunkIndex - 1].data = new Uint8Array(response.body);
          if (response.loaded) {
            progress = response.loaded;
            this.downloadChunks[arrIndex].data[chunkIndex - 1].progress = progress;
            if (this.downloadChunks[arrIndex].status === 'paused' || this.downloadChunks[arrIndex].status === 'cancelled') {
              if (this.downloadSubscriptions[downloadId]) {
                this.downloadSubscriptions[downloadId].unsubscribe();
              }

              return;
            }
          }
        },
        error: () => {
          // Retry the download if needed
          if (retryCount < this.MAX_DOWNLOAD_RETRY) {
            this.getChunkToDownload(downloadId, chunkIndex, retryCount + 1);
          } else {
            this.downloadChunks[arrIndex].status = 'failed';
            this.downloadItems$.next(this.downloadChunks);
          }
        },
        complete: () => {
          this.downloadChunks[arrIndex].data[chunkIndex - 1].status = 'completed';
          if (this.isEncrypted) {
            const { data } = this.downloadChunks[arrIndex].data[chunkIndex - 1];
            SgeCrypto.decryptFileChunk(downloadId, data, chunkIndex, this.decyrptCallback.bind(this));
          } else {
            this.checkIfAnyChunkLeftToDownload(downloadId, arrIndex);
          }
        },
      }),
    );
  }

  private downloadObjects(downloadId: string, retryCount = 0): void {
    this.downloadSubscriptions[downloadId] = new Subscription();
    let chunkData: Uint8Array;
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === downloadId);
    this.downloadSubscriptions[downloadId].add(
      this.downloadObjectsAPI(downloadId).subscribe({
        next: (response) => {
          let progress = 0;
          chunkData = new Uint8Array(response.body);
          if (response.loaded) {
            progress = response.loaded;
          }
          this.downloadChunks[arrIndex].progress = progress;
          this.downloadItems$.next(this.downloadChunks);
          this.isDownloading = true;
        },
        error: () => {
          if (retryCount < this.MAX_DOWNLOAD_RETRY) {
            this.downloadObjects(downloadId, retryCount + 1);
          } else {
            this.isDownloading = false;
            this.downloadChunks[arrIndex].status = 'failed';
          }
        },
        complete: () => {
          this.downloadChunks[arrIndex].status = 'downloaded';
          this.downloadItems$.next(this.downloadChunks);
          this.completeDownload(downloadId, arrIndex);
          const blob = new Blob([chunkData], { type: 'application/octet-stream' });
          this.saveObject(blob);
        },
      }),
    );
  }

  private decyrptCallback(fileId: string, chunkIndex: number, decryptedData: Uint8Array): void {
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === fileId);
    this.downloadChunks[arrIndex].data[chunkIndex - 1].data = decryptedData;
    this.checkIfAnyChunkLeftToDownload(fileId, arrIndex);
  }

  checkIfAnyChunkLeftToDownload(downloadId: string, arrIndex: number): void {
    if (this.getNotDownloadedChunks(downloadId).length > 0) {
      this.getChunkToDownload(downloadId, this.getNotDownloadedChunks(downloadId)[0]);
    } else if (this.allChunksDownloaded(this.downloadChunks[arrIndex])) {
      this.assembleFile(this.downloadChunks[arrIndex]);
      this.completeDownload(downloadId, arrIndex);

      return;
    }
  }

  completeDownload(downloadId: string, arrIndex: number): void {
    this.downloadCompleteAPI(downloadId).subscribe({
      next: () => {
        this.downloadChunks[arrIndex].status = 'downloaded';
        this.downloadItems$.next(this.downloadChunks);
        SgeCrypto.finishFileDecryption(downloadId);
        this.isDownloading = false;
      },
      complete: () => {
        this.notifier$.next();
        this.notifier$.complete();
        this.isDownloading = false;
      },
    });
  }

  getNotDownloadedChunks(downloadId: string): number[] {
    const chunk = this.downloadChunks.filter((chunk) => chunk.downloadId === downloadId)[0];
    const notDownloadedChunks = chunk.data.filter((chunk) => chunk.status === 'pending').map((chunk) => chunk.chunkIndex);

    return notDownloadedChunks;
  }

  allChunksDownloaded(item: DownloadModel): boolean {
    if (item.chunkCount === item.data.length) {
      return item.data.every((chunk) => chunk.status === 'completed');
    }

    return false;
  }

  assembleFile(item: DownloadModel): void {
    this.uniteChunks(item.data).then((fileData) => {
      const blob = new Blob([fileData], { type: 'application/octet-stream' });
      this.saveObject(blob, item);
    });
  }

  saveObject(fileData: Blob, item?: DownloadModel): void {
    const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB chunk size
    const blobParts: BlobPart[] = [];
    let offset = 0;

    while (offset < fileData.size) {
      const chunk = fileData.slice(offset, offset + CHUNK_SIZE);
      blobParts.push(chunk);
      offset += chunk.size;
    }
    const blob = new Blob(blobParts, { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = (item && item.fileName) ?? 'safir-depo.zip';
    anchor.click();
    URL.revokeObjectURL(url);
    this.isDownloading = false;
  }

  async uniteChunks(chunks: DownloadInfoModel[]): Promise<Blob> {
    const blobParts: BlobPart[] = [];
    let offset = 0;

    for (const chunk of chunks) {
      blobParts.push(chunk.data);
      offset += chunk.data.length;
    }

    return new Blob(blobParts);
  }

  cancelDownloads(): void {
    this.downloadChunks.forEach((chunk) => {
      chunk.status = 'cancelled';
    });
    this.downloadSubscriptions = {};
    this.downloadChunks = [];
    this.downloadItems$.next([]);
  }

  cancelDownload(downloadId: string): void {
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === downloadId);
    if (arrIndex !== -1) {
      this.downloadChunks[arrIndex].status = 'cancelled';
      this.downloadChunks.splice(arrIndex, 1);
      this.downloadItems$.next(this.downloadChunks);
    }
    this.unSubscribeDownload(downloadId);
  }

  pauseDownload(downloadId: string): void {
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === downloadId);
    if (arrIndex !== -1) {
      this.downloadChunks[arrIndex].status = 'paused';
      this.downloadItems$.next(this.downloadChunks);
    }
    this.downloadChunks[arrIndex].data = this.downloadChunks[arrIndex].data.map((chunk) => {
      if (chunk.status === 'downloading') {
        chunk.status = 'pending';
        chunk.progress = 0;
      }

      return chunk;
    });
    this.unSubscribeDownload(downloadId);
  }

  resumeDownload(downloadId: string): void {
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === downloadId);
    if (arrIndex > -1) {
      this.downloadChunks[arrIndex].status = 'pending';
      this.downloadItems$.next(this.downloadChunks);
    }
    this.sendChunk(downloadId);
  }

  retryDownload(downloadId: string): void {
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === downloadId);
    if (arrIndex > -1) {
      this.downloadChunks[arrIndex].status = 'pending';
      this.downloadItems$.next(this.downloadChunks);
    }
    this.sendChunk(downloadId);
  }

  retryAllDownloads(): void {
    const failedDownloads = this.downloadChunks.filter((chunk) => chunk.status === 'failed' && !chunk.zippedObject);
    failedDownloads.forEach((chunk) => {
      chunk.status = 'pending';
    });
    this.downloadItems$.next(this.downloadChunks);
    this.sendChunk(failedDownloads[0].downloadId);
  }

  unSubscribeDownload(downloadId: string): void {
    if (this.downloadSubscriptions[downloadId]) {
      this.downloadSubscriptions[downloadId].unsubscribe();
    }
    delete this.downloadSubscriptions[downloadId];
  }

  // Endpoints

  downloadInitAPI(initObject: DownloadInitModel): Observable<DownloadInitResponseModel> {
    return this.http.post<DownloadInitResponseModel>(`${myGlobals.Config.API}/s3/downloads`, initObject);
  }

  downloadChunkAPI(downloadId: string, chunkIndex: number): Observable<any> {
    return this.http.post(`${myGlobals.Config.API}/s3/downloads/${downloadId}/${chunkIndex}`, {}, { responseType: 'arraybuffer', reportProgress: true, observe: 'events' });
  }

  downloadCompleteAPI(downloadId: string): Observable<any> {
    return this.http.put(`${myGlobals.Config.API}/s3/downloads/${downloadId}/complete`, {}).pipe(takeUntil(this.notifier$));
  }

  downloadObjectsAPI(downloadId: string): Observable<any> {
    return this.http.post(`${myGlobals.Config.API}/s3/downloads/${downloadId}/multiple`, { downloadId }, { responseType: 'arraybuffer', reportProgress: true, observe: 'events' });
  }

  publicDownloadObjectsAPI(initObject: DownloadInitModel, downloadId: string, shareId: string, objectId: string): Observable<any> {
    return this.http.post(`${myGlobals.Config.API}/shares/public/${shareId}/${objectId}/${downloadId}/multiple`, initObject, {
      responseType: 'arraybuffer',
      reportProgress: true,
      observe: 'events',
    });
  }

  publicDownloadInitAPI(initObject: DownloadInitModel): Observable<DownloadInitResponseModel> {
    return this.http.post<DownloadInitResponseModel>(`${myGlobals.Config.API}/shares/public/download-init/${this.shareId}`, initObject);
  }

  publicCommonMethodSubscription(downloadInit: any, selectedItems: any[]): void {
    this.subscription = this.publicDownloadInitAPI(downloadInit).subscribe({
      next: (response) => {
        const chunks: DownloadInfoModel[] = [];
        for (let i = 1; i <= response.chunkCount; i++) {
          const data: DownloadInfoModel = {
            chunkIndex: i,
            data: new Uint8Array(),
            status: 'pending',
            progress: 0,
          };
          chunks.push(data);
        }
        const data: DownloadModel = {
          downloadId: response.downloadId,
          status: 'pending',
          data: selectedItems.length === 1 && !selectedItems[0].directory ? chunks : [],
          chunkCount: response.chunkCount,
          fileName: selectedItems.length === 1 && !selectedItems[0].directory ? selectedItems[0].objectName : 'safir-depo.zip',
          totalSize: response.totalSize,
          progress: 0,
          zippedObject: selectedItems.length > 1 || selectedItems[0].directory,
        };
        this.downloadChunks.push(data);
        if (selectedItems.length === 1 && !selectedItems[0].directory) {
          if (this.isEncrypted) {
            SgeCrypto.startFileDecryption(response.downloadId, selectedItems[0].encryptedId)
              .then(() => {
                this.publicSendChunk(response.downloadId);
              })
              .catch(() => {
                const message = {
                  message: 'Bir hata oluştu. Lütfen tekrar deneyiniz.',
                };
                this.notification.errorNotification(message);
              });
          } else {
            this.publicSendChunk(response.downloadId);
          }
        } else if (selectedItems.length > 1 || (selectedItems.length === 1 && selectedItems[0].directory)) {
          this.publicDownloadObjects(response.downloadId);

          return;
        }
      },
    });
  }

  publicDownloadInit(selectedItems: any[] = []): void {
    for (const obj of selectedItems) {
      this.isEncrypted = obj.encrypted;
    }
    const downloadInit: DownloadInitModel = {} as DownloadInitModel;
    downloadInit.applicationName = 'storage';
    downloadInit.downloadType = 'ATTACHMENT';
    downloadInit.password = this.password;
    downloadInit.objects = selectedItems.map((item) => item.objectId);
    if (fs.isSupported()) {
      //using fs
      let file!: any;
      if (selectedItems.length === 1) {
        fs.createDummyFile(selectedItems[0]?.objectId, selectedItems[0]?.size)
          .then(() => {
            //success
            this.publicCommonMethodSubscription(downloadInit, selectedItems);
          })
          .catch((error) => {
            this.notification.errorNotification({ message: this.commonService.getTranslatedMessage('DOWNLOAD_SIZE_TO_BIG_FOR_CHROME') });
          });
      } else {
        this.publicCommonMethodSubscription(downloadInit, selectedItems);
      }
    } else {
      //Using ArrayBuffer
      let file!: any;
      try {
        file = new Uint8Array(selectedItems[0].size); //firefox supports max  8.5 gb
        this.publicCommonMethodSubscription(downloadInit, selectedItems);
      } catch {
        this.isDownloading = false;
        this.notification.errorNotification({ message: this.commonService.getTranslatedMessage('DOWNLOAD_SIZE_TO_BIG_FOR_FIREFOX') });
      }
    }
  }

  publicSendChunk(downloadId: string): void {
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === downloadId);
    const pendingChunks = this.downloadChunks[arrIndex].data.filter((chunk) => chunk.status === 'pending');
    const startIndex = pendingChunks.sort((a, b) => a.chunkIndex - b.chunkIndex)[0].chunkIndex;
    const numberOfChunks =
      startIndex + this.NUMBER_OF_CHUNKS > this.downloadChunks[arrIndex].data.length ? this.downloadChunks[arrIndex].data.length : startIndex + this.NUMBER_OF_CHUNKS;
    for (let chunkIndex = startIndex; chunkIndex <= numberOfChunks; chunkIndex++) {
      this.getPublicChunkToDownload(downloadId, chunkIndex);
    }
  }

  private getPublicChunkToDownload(downloadId: string, chunkIndex: number, retryCount = 0): void {
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === downloadId);
    const chunkData = this.downloadChunks[arrIndex].data[chunkIndex - 1];
    const chunk = this.downloadChunks[arrIndex];
    if (chunkData.status !== 'completed' && chunk.status !== 'paused' && chunk.status !== 'cancelled') {
      this.publicDownloadChunk(downloadId, arrIndex, chunkIndex, retryCount);
    }
  }

  private publicDownloadChunk(downloadId: string, arrIndex: number, chunkIndex: number, retryCount = 0): void {
    const passwordModel: PasswordModel = {} as PasswordModel;
    passwordModel.password = this.password;
    this.downloadSubscriptions[downloadId] = new Subscription();
    let progress = 0;
    this.downloadSubscriptions[downloadId].add(
      this.publicDownloadChunkAPI(passwordModel, this.shareId, this.objectId, downloadId, chunkIndex).subscribe({
        next: (response) => {
          this.downloadChunks[arrIndex].data[chunkIndex - 1].status = 'downloading';
          this.downloadChunks[arrIndex].data[chunkIndex - 1].data = new Uint8Array(response.body);
          if (response.loaded) {
            progress = response.loaded;
            this.downloadChunks[arrIndex].data[chunkIndex - 1].progress = progress;
            if (this.downloadChunks[arrIndex].status === 'paused' || this.downloadChunks[arrIndex].status === 'cancelled') {
              if (this.downloadSubscriptions[downloadId]) {
                this.downloadSubscriptions[downloadId].unsubscribe();
              }

              return;
            }
          }
        },
        error: () => {
          // Retry the download if needed
          if (retryCount < this.MAX_DOWNLOAD_RETRY) {
            this.getPublicChunkToDownload(downloadId, chunkIndex, retryCount + 1);
          } else {
            this.downloadChunks[arrIndex].status = 'failed';
            this.downloadItems$.next(this.downloadChunks);
          }
        },
        complete: () => {
          this.downloadChunks[arrIndex].data[chunkIndex - 1].status = 'completed';
          if (this.isEncrypted) {
            const { data } = this.downloadChunks[arrIndex].data[chunkIndex - 1];
            SgeCrypto.decryptFileChunk(downloadId, data, chunkIndex, this.publicDecyrptCallback.bind(this));
          } else {
            this.publicCheckIfAnyChunkLeftToDownload(downloadId, arrIndex);
          }
        },
      }),
    );
  }

  private publicDownloadObjects(downloadId: string, retryCount = 0): void {
    this.downloadSubscriptions[downloadId] = new Subscription();
    let chunkData: Uint8Array;
    const downloadInit: DownloadInitModel = {} as DownloadInitModel;
    downloadInit.password = this.password;
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === downloadId);
    this.downloadSubscriptions[downloadId].add(
      this.publicDownloadObjectsAPI(downloadInit, downloadId, this.shareId, this.objectId).subscribe({
        next: (response) => {
          let progress = 0;
          chunkData = new Uint8Array(response.body);
          if (response.loaded) {
            progress = response.loaded;
          }
          this.downloadChunks[arrIndex].progress = progress;
          this.downloadItems$.next(this.downloadChunks);
        },
        error: () => {
          if (retryCount < this.MAX_DOWNLOAD_RETRY) {
            this.publicDownloadObjects(downloadId, retryCount + 1);
          } else {
            this.downloadChunks[arrIndex].status = 'failed';
          }
        },
        complete: () => {
          this.downloadChunks[arrIndex].status = 'downloaded';
          this.downloadItems$.next(this.downloadChunks);
          this.publicCompleteDownload(downloadId, arrIndex);
          const blob = new Blob([chunkData], { type: 'application/octet-stream' });
          this.saveObject(blob);
        },
      }),
    );
  }

  publicCompleteDownload(downloadId: string, arrIndex: number): void {
    this.publicDownloadCompleteAPI(downloadId).subscribe({
      next: () => {
        this.downloadChunks[arrIndex].status = 'downloaded';
        this.downloadItems$.next(this.downloadChunks);
        SgeCrypto.finishFileDecryption(downloadId);
      },
      complete: () => {
        this.notifier$.next();
        this.notifier$.complete();
      },
    });
  }

  publicDownloadCompleteAPI(downloadId: string): Observable<any> {
    return this.http.put(`${myGlobals.Config.API}/shares/public/${downloadId}/complete/${this.shareId}`, {});
  }

  publicDownloadChunkAPI(passwordModel: PasswordModel, shareId: string, objectId: string, downloadId: string, chunkIndex: number): Observable<any> {
    return this.http.post(`${myGlobals.Config.API}/shares/public/download-chunk/${shareId}/object/${objectId}/${chunkIndex}/${downloadId}`, passwordModel, {
      responseType: 'arraybuffer',
      reportProgress: true,
      observe: 'events',
    });
  }

  private publicDecyrptCallback(fileId: string, chunkIndex: number, decryptedData: Uint8Array): void {
    const arrIndex = this.downloadChunks.findIndex((chunk) => chunk.downloadId === fileId);
    this.downloadChunks[arrIndex].data[chunkIndex - 1].data = decryptedData;
    this.publicCheckIfAnyChunkLeftToDownload(fileId, arrIndex);
  }

  publicCheckIfAnyChunkLeftToDownload(downloadId: string, arrIndex: number): void {
    if (this.getNotDownloadedChunks(downloadId).length > 0) {
      this.getPublicChunkToDownload(downloadId, this.getNotDownloadedChunks(downloadId)[0]);
    } else if (this.allChunksDownloaded(this.downloadChunks[arrIndex])) {
      this.assembleFile(this.downloadChunks[arrIndex]);
      this.publicCompleteDownload(downloadId, arrIndex);

      return;
    }
  }
}
